import { useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import { useTranslation } from 'react-i18next';
import api from '../helpers/api';

const mapAssetToOption = (asset) => ({
    value: asset.id,
    label: asset.assetName,
    assetNumber: asset.assetNumber,
    serialNumber: asset.serialNumber,
    childAsset: asset.childAsset,
    raw: asset,
});

const ParentAssetSelector = ({
    value,
    onChange,
    excludeIds = [],
    selectStyles,
    hasError = false,
    placeholder,
    isDisabled = false,
}) => {
    const { t } = useTranslation();

    const fetchAssets = useCallback(async (search = '') => {
        try {
            const params = { count: 10 };
            if (search) params.search = search;
            const { data } = await api.get('asset/get-assets-info', { params });
            return (data?.response || [])
                .filter((a) => !excludeIds.includes(a.id))
                .map(mapAssetToOption);
        } catch (err) {
            console.error('Error fetching parent assets:', err);
            return [];
        }
    }, [excludeIds]);

    const selectedOption = value ? mapAssetToOption(value) : null;

    const formatOptionLabel = (option) => (
        <div className="d-flex align-items-center justify-content-between">
            <div>
                <div className="fw-medium">{option.label}</div>
                {option.serialNumber && (
                    <small className="text-muted">{option.serialNumber}</small>
                )}
            </div>
            {option.childAsset !== undefined && (
                <span
                    className={`badge ms-2 ${option.childAsset ? 'bg-warning-subtle text-warning' : 'bg-success-subtle text-success'}`}
                >
                    {option.childAsset
                        ? t('shared.sub_asset', 'Sub Asset')
                        : t('business.assets.parent_asset', 'Parent Asset')}
                </span>
            )}
        </div>
    );

    return (
        <AsyncSelect
            defaultOptions
            loadOptions={fetchAssets}
            value={selectedOption}
            onChange={(opt) => onChange(opt ? opt.raw : null)}
            formatOptionLabel={formatOptionLabel}
            placeholder={placeholder || t('business.assets.search_parent_asset', 'Search for parent asset...')}
            isClearable
            isSearchable
            isDisabled={isDisabled}
            classNamePrefix="react-select"
            styles={selectStyles ? selectStyles(hasError) : undefined}
            noOptionsMessage={() => t('shared.no_assets_found', 'No assets found')}
            loadingMessage={() => t('shared.loading', 'Loading...')}
        />
    );
};

export default ParentAssetSelector;
